/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Course, Quiz, Badge, LeaderboardEntry, BlogPost, FAQItem, SubscriptionPlan } from '../types';

export const MOCK_BADGES: Badge[] = [
  {
    id: 'first_step',
    name: 'First Step',
    description: 'Enrolled in your first premium learning course.',
    iconName: 'BookOpen',
    category: 'course',
    requirementText: 'Enroll in any course'
  },
  {
    id: 'streak_3',
    name: 'Consistency Kickstart',
    description: 'Maintained a daily learning streak for 3 days.',
    iconName: 'Flame',
    category: 'streak',
    requirementText: '3-day daily streak'
  },
  {
    id: 'streak_7',
    name: 'Weekly Warrior',
    description: 'Maintained a daily learning streak for 7 consecutive days.',
    iconName: 'Flame',
    category: 'streak',
    requirementText: '7-day daily streak'
  },
  {
    id: 'quiz_master',
    name: 'Century Club',
    description: 'Earned 100+ XP in a single full mock examination.',
    iconName: 'Award',
    category: 'quiz',
    requirementText: 'Earn 100+ XP in a single mock test'
  },
  {
    id: 'xp_level_1',
    name: 'Rising Star',
    description: 'Reached a cumulative total of 500 XP.',
    iconName: 'Zap',
    category: 'xp',
    requirementText: 'Reach 500 total XP'
  },
  {
    id: 'xp_level_2',
    name: 'Grandmaster Spark',
    description: 'Reached a cumulative total of 1500 XP.',
    iconName: 'Sparkles',
    category: 'xp',
    requirementText: 'Reach 1500 total XP'
  }
];

export const MOCK_COURSES: Course[] = [
  {
    id: 'course_1',
    title: 'Advanced Data Structures & Algorithms Mastery',
    tagline: 'Crack elite software developer interviews with visual graph theory and tree patterns.',
    description: 'The ultimate algorithms course packed with interactive dynamic programming, tree traversals, back-tracking, and visual mock quizzes. Prepared for developers targeting top technical roles.',
    instructorName: 'Dr. Evelyn Carter',
    instructorTitle: 'Former Staff Engineer at Google',
    coverImage: 'https://images.unsplash.com/photo-1516116211223-5c359a36298a?auto=format&fit=crop&q=80&w=800',
    rating: 4.9,
    enrolledCount: 1420,
    lessonsCount: 4,
    totalDuration: '5h 15m',
    category: 'Computer Science',
    level: 'Advanced',
    price: 49,
    isPremium: true,
    lessons: [
      {
        id: 'lesson_1_1',
        title: 'Introduction to Amortized Complexity Analysis',
        description: 'Understand actual time complexities over long streams of operations using Banker\'s and Physicist\'s methods.',
        duration: '12:45',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', // Placeholder embed
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Amortized Analysis Explained\n\nAmortized analysis is used for algorithms where an occasional operation is very slow, but most operations are very fast. Instead of looking at the *worst-case* of a single operation, we look at the *average-case* of a sequence of operations.\n\n#### Methods:\n1. **Aggregate Method**: Total cost of $T(n)$ operations divided by $n$.\n2. **Accounting (Banker\'s) Method**: Charge extra for fast operations and save the "surplus" as credits to pay for expensive operations later.\n3. **Potential (Physicist\'s) Method**: Define a potential function that tracks the "stored energy" of the data structure.`
      },
      {
        id: 'lesson_1_2',
        title: 'Mastering Binary Search Trees & AVL Rotations',
        description: 'Deep dive into self-balancing binary trees, height definitions, and complex rotational pivot mechanics.',
        duration: '18:20',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### AVL Trees: Self-Balancing BSTs\n\nAn AVL tree is a self-balancing Binary Search Tree where the difference between heights of left and right subtrees cannot be more than **1** for all nodes.\n\n#### Rotations:\n- **Left Rotation (L)**: Used when a node is inserted into the right subtree of a right child.\n- **Right Rotation (R)**: Used when insertion is in the left subtree of a left child.\n- **Left-Right (LR)**: Double rotation for zigzag insertion.\n- **Right-Left (RL)**: Double rotation for zigzag insertion.`
      },
      {
        id: 'lesson_1_3',
        title: 'Dynamic Programming: The Matrix Chain Multiplication',
        description: 'Learn the optimal parenthesis placement formulation to minimize scalar multiplications.',
        duration: '22:10',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Matrix Chain Multiplication (MCM)\n\nMCM is a classic dynamic programming optimization problem. Given a sequence of matrices, we want to find the most efficient way to multiply them together.\n\n#### Formula:\nFor a matrix dimension list $p$, let $m[i,j]$ be the minimum number of scalar multiplications needed to compute the matrix $A_{i..j}$:\n\n$$m[i,j] = \\min_{i \\le k < j} \\{m[i,k] + m[k+1,j] + p_{i-1}p_kp_j\\}$$`
      },
      {
        id: 'lesson_1_4',
        title: 'Graph Theory: Network Flow & Ford-Fulkerson Method',
        description: 'Analyze residual capacities, augmenting paths, and min-cut max-flow equivalence theorems.',
        duration: '28:40',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Network Flow & Ford-Fulkerson\n\nThe Ford-Fulkerson algorithm computes the maximum flow in a flow network. It greedily finds augmenting paths from source to sink in the residual graph and pushes flow along them.\n\n#### Max-Flow Min-Cut Theorem:\nIn any network, the value of the maximum flow is equal to the capacity of the minimum cut.`
      }
    ]
  },
  {
    id: 'course_2',
    title: 'Academic Class 8 Science',
    tagline: 'Complete Study Pack',
    description: 'Embark on an exciting journey through the fascinating world of science. Master every chapter with expertly curated Study Resources designed to strengthen concepts, encourage scientific thinking, and prepare you confidently for every Academic examination.',
    instructorName: 'Sarah Jenkins',
    instructorTitle: 'Core React Contributor & Designer',
    coverImage: 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?auto=format&fit=crop&q=80&w=800',
    rating: 4.9,
    ratingText: 'Trusted by Academic Students',
    enrolledCount: 2310,
    lessonsCount: 3,
    totalDuration: 'Complete Study Pack',
    category: '📚 Academic Science',
    level: 'Intermediate',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    isBestseller: true,
    classLimit: 'Class 8',
    features: [
      'Complete Chapter-wise Coverage',
      'Exam-Oriented Practice',
      'Latest Academic Curriculum'
    ],
    lessons: [
      {
        id: 'lesson_2_1',
        title: 'Exploring the Wonderful World of Science',
        description: 'Understand scientific inquiry, investigative methodologies, lab protocols, and scientific discoveries.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Exploring the Wonderful World of Science\n\nScience is not just a collection of facts, but a process of inquiry and investigation to understand the natural world.\n\n#### The Scientific Method:\n1. **Observation**: Noticing phenomena and formulating specific questions.\n2. **Hypothesis Formulation**: Proposing a tentative explanation that can be tested.\n3. **Experimentation**: Designing controlled tests to gather experimental data.\n4. **Data Analysis**: Evaluating observations and identifying patterns.\n5. **Drawing Conclusions**: Confirming, modifying, or rejecting the initial hypothesis.`
      },
      {
        id: 'lesson_2_2',
        title: 'The Invisible World : Beyond our Naked Eyes',
        description: 'Explore the microscopic world of bacteria, fungi, algae, and protozoa, and their impact on our lives.',
        duration: 'Chapter 2',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### The Invisible World : Beyond our Naked Eyes\n\nMicroorganisms are tiny organisms that cannot be seen with the naked eye. They are classified into four major groups: Bacteria, Fungi, Protozoa, and some Algae.\n\n#### Friendly Microorganisms:\n- **Lactobacillus**: Helps in the formation of curd from milk.\n- **Yeast**: Used in baking and commercial production of alcohol.\n- **Antibiotics & Vaccines**: Protect humans and animals from various diseases.\n\n#### Harmful Microorganisms (Pathogens):\n- Cause diseases like tuberculosis, cholera, and malaria.\n- Cause food spoilage and plant diseases.`
      },
      {
        id: 'lesson_2_3',
        title: 'Health: The Ultimate Treasure',
        description: 'Explore human health, nutrition, immunization, and how to maintain life-long physical and mental wellbeing.',
        duration: 'Chapter 3',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Health: The Ultimate Treasure\n\nHealth is a state of complete physical, mental, and social well-being, and not merely the absence of disease.\n\n#### Maintaining Good Health:\n1. **Balanced Diet**: Consuming adequate proportions of carbohydrates, proteins, fats, vitamins, and minerals.\n2. **Personal Hygiene**: Regular cleaning, hand washing, and maintaining sanitation.\n3. **Physical Exercise**: Regular workouts and active lifestyles for structural fitness.\n4. **Immunization**: Timely vaccines to build natural defenses against contagious illnesses.`
      }
    ]
  },
  {
    id: 'course_3',
    title: 'Quantitative Aptitude & Mathematical Logic for Competitions',
    tagline: 'Master rapid logical calculation methods, permutation matrices, and competitive probability.',
    description: 'A premium fast-paced logical training program focusing on speed calculation, shortcuts for probability, complex number arrays, and mathematical inductions for examinations.',
    instructorName: 'Prof. Alistair Vance',
    instructorTitle: 'Olympiad Mathematics Chair',
    coverImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=800',
    rating: 4.7,
    enrolledCount: 1845,
    lessonsCount: 2,
    totalDuration: '2h 15m',
    category: 'Mathematics',
    level: 'Beginner',
    price: 29,
    isPremium: false,
    lessons: [
      {
        id: 'lesson_3_1',
        title: 'Rapid Modular Arithmetic & Remainder Theorems',
        description: 'Apply Chinese Remainder Theorem and Fermat\'s Little Theorem in fast competitive exams.',
        duration: '15:15',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Modular Arithmetic Shortcuts\n\nUseful properties for quick computations:\n\n1. **Fermat\'s Little Theorem**: If $p$ is a prime number, then for any integer $a$:\n   $$a^p \\equiv a \\pmod p$$\n   And if $a$ is not divisible by $p$:\n   $$a^{p-1} \\equiv 1 \\pmod p$$\n\n2. **Chinese Remainder Theorem**: Solves systems of simultaneous congruences with pairwise coprime moduli.`
      },
      {
        id: 'lesson_3_2',
        title: 'Permutations & Circular Combinatorics',
        description: 'Examine circular arrangements, necklaces, and standard inclusion-exclusion parameters.',
        duration: '22:30',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Circular Permutations\n\nThe number of ways to arrange $n$ distinct objects in a circle is:\n$$(n - 1)!$$\n\nIf the circle can be flipped (like a necklace where clockwise and counterclockwise are identical):\n$$\\frac{(n - 1)!}{2}$$`
      }
    ]
  },
  {
    id: 'course_6_science',
    title: 'Academic Class 6 Science',
    tagline: 'Explore the Secrets of Nature',
    description: 'A beautifully structured science curriculum for Class 6 students. From The Wonderful World of Science to Diversity in the Living World, learn everything with visual aids and fun experiments.',
    instructorName: 'Sarah Jenkins',
    instructorTitle: 'Lead Science Faculty',
    coverImage: 'https://images.unsplash.com/photo-1516116211223-5c359a36298a?auto=format&fit=crop&q=80&w=800',
    rating: 4.8,
    ratingText: 'Highly Recommended for Class 6',
    enrolledCount: 1200,
    lessonsCount: 1,
    totalDuration: 'Chapter-wise Study',
    category: '📚 Academic Science',
    level: 'Beginner',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 6',
    features: ['Chapter notes', 'Interactive exercises'],
    lessons: [
      {
        id: 'lesson_6_s1',
        title: 'The Wonderful World of Science',
        description: 'Understand the sources of food, plant parts as food, and animal products as food.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### The Wonderful World of Science\n\nAll living organisms require food to survive, grow, and perform vital body functions.\n\n#### Key Food Sources:\n1. **Plants**: Roots, stems, leaves, flowers, seeds, and fruits are plant parts used as food.\n2. **Animals**: Milk, meat, honey, eggs, and fish.`
      }
    ]
  },
  {
    id: 'course_6_math',
    title: 'Academic Class 6 Mathematics',
    tagline: 'Mastering Numbers & Logic',
    description: 'Get a rock-solid foundation in mathematics with Class 6 Academic concepts including Patterns in Mathematics, Lines and Angles, and Number Play.',
    instructorName: 'Prof. Alistair Vance',
    instructorTitle: 'Olympiad Mathematics Chair',
    coverImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=800',
    rating: 4.9,
    ratingText: 'Excel in School Exams',
    enrolledCount: 1540,
    lessonsCount: 1,
    totalDuration: 'Complete Math Lab',
    category: 'Mathematics',
    level: 'Beginner',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 6',
    features: ['Step-by-step solved problems', 'Practice tests'],
    lessons: [
      {
        id: 'lesson_6_m1',
        title: 'Patterns in Mathematics',
        description: 'Learn large numbers, place value chart, Indian and International system, and estimation.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Patterns in Mathematics\n\nNumbers are highly useful in counting objects, comparing quantities, and representing details.\n\n#### Place Value Charts:\n- **Indian System**: Ones, Tens, Hundreds, Thousands, Ten Thousands, Lakhs, Ten Lakhs, Crores.\n- **International System**: Ones, Tens, Hundreds, Thousands, Ten Thousands, Hundred Thousands, Millions, Ten Millions.`
      }
    ]
  },
  {
    id: 'course_6_social',
    title: 'Academic Class 6 Social Science',
    tagline: 'Journey Through History & Civic Life',
    description: 'Explore the ancient world, earth our habitat, and social and political life designed specifically for Class 6 Academic.',
    instructorName: 'Aditya Sharma',
    instructorTitle: 'Senior Social Studies Mentor',
    coverImage: 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?auto=format&fit=crop&q=80&w=800',
    rating: 4.7,
    ratingText: 'Trusted Syllabus',
    enrolledCount: 1100,
    lessonsCount: 1,
    totalDuration: 'Comprehensive Pack',
    category: 'Social Science',
    level: 'Beginner',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 6',
    features: ['Historical maps', 'Civics flowcharts'],
    lessons: [
      {
        id: 'lesson_6_ss1',
        title: 'What, Where, How and When?',
        description: 'An introduction to how we study history, archaeological and literary sources.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### What, Where, How and When?\n\nHistory is a systematic study of past events. It tells us about the life, food habits, and cultures of ancient people.`
      }
    ]
  },
  {
    id: 'course_7_science',
    title: 'Academic Class 7 Science',
    tagline: 'Nurture Your Scientific Temperament',
    description: 'Diving deeper into advanced topics of The Ever-Evolving World of Science, Exploring Substances, and Heat Transfer for Academic Class 7.',
    instructorName: 'Sarah Jenkins',
    instructorTitle: 'Lead Science Faculty',
    coverImage: 'https://images.unsplash.com/photo-1532187863486-abf9d39d66e8?auto=format&fit=crop&q=80&w=800',
    rating: 4.8,
    ratingText: 'Top Quality Visual Lessons',
    enrolledCount: 1300,
    lessonsCount: 1,
    totalDuration: 'Advanced Chapter-wise',
    category: '📚 Academic Science',
    level: 'Intermediate',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 7',
    features: ['Visual explanations', 'Revision notes'],
    lessons: [
      {
        id: 'lesson_7_s1',
        title: 'The Ever-Evolving World of Science',
        description: 'Learn autotrophic and heterotrophic nutrition, and the complete mechanism of photosynthesis.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### The Ever-Evolving World of Science\n\nNutrients enable living organisms to build their bodies, grow, repair damaged parts, and provide energy.\n\n#### Photosynthesis:\nGreen plants make their own food through photosynthesis in the presence of sunlight, water, carbon dioxide, and chlorophyll.`
      }
    ]
  },
  {
    id: 'course_7_math',
    title: 'Academic Class 7 Mathematics',
    tagline: 'Fractions, Decimals, & Integers Mastered',
    description: 'Advance your analytical math skills. Covers integers, fractions, decimals, data handling, and simple equations.',
    instructorName: 'Prof. Alistair Vance',
    instructorTitle: 'Olympiad Mathematics Chair',
    coverImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=800',
    rating: 4.9,
    ratingText: 'Perfect for Scoring 100%',
    enrolledCount: 1400,
    lessonsCount: 1,
    totalDuration: 'Classroom Lectures',
    category: 'Mathematics',
    level: 'Intermediate',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 7',
    features: ['Solved board queries', 'Formulas card'],
    lessons: [
      {
        id: 'lesson_7_m1',
        title: 'Integers: Properties & Calculations',
        description: 'Understand addition, subtraction, multiplication, and division properties of integers.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Properties of Integers\n\n- **Closure Property**: Integers are closed under addition, subtraction, and multiplication.\n- **Commutative Property**: $a + b = b + a$`
      }
    ]
  },
  {
    id: 'course_7_social',
    title: 'Academic Class 7 Social Science',
    tagline: 'Medieval India & Our Environment',
    description: 'Understand medieval history, environmental geography, and social equality frameworks in India.',
    instructorName: 'Aditya Sharma',
    instructorTitle: 'Senior Social Studies Mentor',
    coverImage: 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?auto=format&fit=crop&q=80&w=800',
    rating: 4.7,
    ratingText: 'Highly Rated Guides',
    enrolledCount: 1050,
    lessonsCount: 1,
    totalDuration: 'Complete Social Guide',
    category: 'Social Science',
    level: 'Intermediate',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 7',
    features: ['Timeline of empires', 'Geography maps'],
    lessons: [
      {
        id: 'lesson_7_ss1',
        title: 'Tracing Changes Through a Thousand Years',
        description: 'Analyze medieval terminology, maps, and historical records over 1000 years.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Medieval Indian History\n\nThe thousand years between 700 and 1750 saw massive social, political, and cultural shifts in the Indian subcontinent.`
      }
    ]
  },
  {
    id: 'course_8_math',
    title: 'Academic Class 8 Mathematics',
    tagline: 'Linear Equations & Quadrilaterals',
    description: 'Perfect resource for mastering Class 8 Academic math topics including A Square and A Cube, Power Play, and A Story of Numbers.',
    instructorName: 'Prof. Alistair Vance',
    instructorTitle: 'Olympiad Mathematics Chair',
    coverImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=800',
    rating: 4.9,
    ratingText: 'Highly Recommended',
    enrolledCount: 1800,
    lessonsCount: 1,
    totalDuration: 'Syllabus coverage',
    category: 'Mathematics',
    level: 'Advanced',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 8',
    features: ['Board pattern practice', 'Detailed formulasheet'],
    lessons: [
      {
        id: 'lesson_8_m1',
        title: 'A Square and A Cube',
        description: 'Master properties of rational numbers, additive and multiplicative identities.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### A Square and A Cube\n\nA rational number is a number that can be expressed as a fraction $p/q$ where $p$ and $q$ are integers and $q$ is not equal to zero.`
      }
    ]
  },
  {
    id: 'course_8_social',
    title: 'Academic Class 8 Social Science',
    tagline: 'Our Pasts, Resources & Development',
    description: 'Master Academic Class 8 social sciences. Learn history, resources, geography, and civic structures with interactive material.',
    instructorName: 'Aditya Sharma',
    instructorTitle: 'Senior Social Studies Mentor',
    coverImage: 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?auto=format&fit=crop&q=80&w=800',
    rating: 4.8,
    ratingText: 'Trusted by school toppers',
    enrolledCount: 1600,
    lessonsCount: 1,
    totalDuration: 'Complete Study Guide',
    category: 'Social Science',
    level: 'Advanced',
    price: 149,
    formattedPrice: '₹149',
    buttonText: '📥 Get Study Pack',
    isPremium: true,
    classLimit: 'Class 8',
    features: ['Interactive timelines', 'Detailed maps'],
    lessons: [
      {
        id: 'lesson_8_ss1',
        title: 'Natural Resources and Their Use',
        description: 'Types, distribution, conservation, and sustainable management of natural resources.',
        duration: 'Chapter 1',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        pdfUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
        notes: `### Natural Resources and Their Use\n\nNatural resources are substances derived from Earth and used by humans.`
      }
    ]
  }
];

export const MOCK_QUIZZES: Quiz[] = [
  {
    id: 'quiz_1',
    title: 'Term I Mock Tests',
    description: 'Practice chapter-wise and term-wise mock tests based on the latest Academic pattern to improve confidence and exam readiness.',
    category: 'Academic Assessment',
    type: 'mock',
    durationMinutes: 45,
    xpAward: 150,
    creditAward: 40,
    badgeText: 'TERM I',
    infoText1: '10 Mock Tests',
    infoText2: '45 Minutes',
    buttonText: 'Start Practice',
    questions: [
      {
        id: 'q1_1',
        text: 'What is the tightest upper bound of the worst-case time complexity of inserting into a self-balancing Red-Black tree with N nodes?',
        options: [
          'O(1)',
          'O(log N)',
          'O(N)',
          'O(N log N)'
        ],
        correctAnswer: 1,
        explanation: 'In a self-balancing Red-Black Tree, the maximum height of a tree with N nodes is 2 * log2(N + 1). Therefore, lookup, insertion, and deletion operations take O(log N) time in the absolute worst-case.',
        difficulty: 'Medium'
      },
      {
        id: 'q1_2',
        text: 'Which amortized cost method utilizes a formal energy storage potential function Φ mapping a state to a real number?',
        options: [
          'Banker\'s Accounting Method',
          'Aggregate Method',
          'Physicist\'s Potential Method',
          'Divide and Conquer Formulation'
        ],
        correctAnswer: 2,
        explanation: 'The Physicist\'s Potential Method models the stored credit as potential energy, using a mathematical function Φ(D) that maps any state of the data structure to a non-negative real value.',
        difficulty: 'Hard'
      },
      {
        id: 'q1_3',
        text: 'In Matrix Chain Multiplication with dimension list p = [10, 20, 30, 40, 30], what is the total number of matrices to be multiplied?',
        options: [
          '5 matrices',
          '4 matrices',
          '3 matrices',
          '8 matrices'
        ],
        correctAnswer: 1,
        explanation: 'Given a dimension list of size k, the number of matrices is k - 1. Since there are 5 dimensions, we have 4 matrices with dimensions 10x20, 20x30, 30x40, and 40x30.',
        difficulty: 'Medium'
      },
      {
        id: 'q1_4',
        text: 'What is the maximum flow capacity of a network if the minimum cut capacity is found to be exactly 45 units?',
        options: [
          '22.5 units',
          'Under 45 units',
          'Exactly 45 units',
          'Over 45 units'
        ],
        correctAnswer: 2,
        explanation: 'By the Max-Flow Min-Cut Theorem, the maximum flow value of any network is exactly equal to the capacity of its minimum cut. Thus, it is exactly 45 units.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_2',
    title: 'Term II Mock Tests',
    description: 'Prepare for the second academic term with comprehensive mock tests covering every prescribed chapter.',
    category: 'Academic Assessment',
    type: 'pyq',
    year: 2025,
    durationMinutes: 45,
    xpAward: 150,
    creditAward: 40,
    badgeText: 'TERM II',
    infoText1: '10 Mock Tests',
    infoText2: '45 Minutes',
    buttonText: 'Start Practice',
    questions: [
      {
        id: 'q2_1',
        text: 'According to Fermat\'s Little Theorem, what is the value of 5^16 modulo 17?',
        options: [
          '1',
          '5',
          '16',
          '4'
        ],
        correctAnswer: 0,
        explanation: 'Fermat\'s Little Theorem states that if p is prime and a is not divisible by p, then a^(p-1) ≡ 1 (mod p). Here p=17 (prime) and a=5, so 5^16 ≡ 1 (mod 17).',
        difficulty: 'Easy'
      },
      {
        id: 'q2_2',
        text: 'How many distinct arrangements can be made with 6 different physical keys placed on a traditional key-ring structure?',
        options: [
          '720 arrangements',
          '120 arrangements',
          '60 arrangements',
          '24 arrangements'
        ],
        correctAnswer: 2,
        explanation: 'For circular permutations where clock-wise and counter-clock-wise divisions represent identical physical rotations (such as keys on a ring), the number of configurations is (n-1)! / 2. Here, (6-1)! / 2 = 5! / 2 = 120 / 2 = 60.',
        difficulty: 'Medium'
      },
      {
        id: 'q2_3',
        text: 'Solve for the remainder when 2^100 is divided by 101. Note: 101 is a prime number.',
        options: [
          '1',
          '2',
          '50',
          '100'
        ],
        correctAnswer: 0,
        explanation: 'Using Fermat\'s Little Theorem: 2^(101-1) ≡ 1 (mod 101). Hence, 2^100 mod 101 is exactly 1.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_3',
    title: 'Chapter Challenge Series',
    description: 'Attempt chapter-wise timed challenges to improve speed, accuracy and conceptual understanding throughout the academic session.',
    category: 'Daily Practice',
    type: 'practice',
    durationMinutes: 15,
    xpAward: 100,
    creditAward: 25,
    badgeText: 'CHAPTER CHALLENGE',
    infoText1: '100+ Challenges',
    infoText2: '15 Minutes',
    buttonText: 'Start Challenge',
    questions: [
      {
        id: 'q3_1',
        text: 'Which React 19 hook is specifically designed to manage state mutations inside native HTML Form action submissions?',
        options: [
          'useMemoState',
          'useActionState',
          'useTransitionState',
          'useFormPending'
        ],
        correctAnswer: 1,
        explanation: 'useActionState (previously named useFormState) is the hook introduced to accept an action function, return its state, and trigger loading indicators naturally.',
        difficulty: 'Easy'
      },
      {
        id: 'q3_2',
        text: 'True or False: React Server Components (RSC) automatically download all their library imports directly to the user client bundle.',
        options: [
          'True',
          'False'
        ],
        correctAnswer: 1,
        explanation: 'False. One of the main benefits of React Server Components is that dependencies used inside Server Components remain on the server, drastically reducing the client-side bundle size.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_6_science',
    title: 'Class 6 Science: The Wonderful World of Science Challenge',
    description: 'Test your understanding of the scope and marvels of scientific inquiry with 4 simple questions.',
    category: '📚 Academic Science',
    type: 'practice',
    durationMinutes: 10,
    xpAward: 50,
    creditAward: 15,
    classLimit: 'Class 6',
    badgeText: 'FOOD SECRETS',
    infoText1: '2 Questions',
    infoText2: '10 Minutes',
    buttonText: 'Attempt Quiz',
    questions: [
      {
        id: 'q6_s1',
        text: 'Which of the following parts of the plant is NOT typically used as food?',
        options: ['Roots', 'Leaves', 'Stems of toxic shrubs', 'Seeds'],
        correctAnswer: 2,
        explanation: 'While roots, leaves, stems, and seeds of many edible plants are consumed, toxic shrubs are poisonous and are never used as food sources.',
        difficulty: 'Easy'
      },
      {
        id: 'q6_s2',
        text: 'Honey is a product of which animal source?',
        options: ['Cattle', 'Honey bees', 'Poultry', 'Silkworm'],
        correctAnswer: 1,
        explanation: 'Honey bees collect nectar from flowers, convert it into honey, and store it in their beehive.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_6_math',
    title: 'Class 6 Mathematics: Lines and Angles Blitz',
    description: 'A rapid quiz testing place values, whole numbers properties, and comparing large numbers.',
    category: 'Mathematics',
    type: 'practice',
    durationMinutes: 10,
    xpAward: 50,
    creditAward: 15,
    classLimit: 'Class 6',
    badgeText: 'NUMBERS CHAMP',
    infoText1: '2 Questions',
    infoText2: '10 Minutes',
    buttonText: 'Start Blitz',
    questions: [
      {
        id: 'q6_m1',
        text: 'In the Indian Place Value system, what comes immediately after Ten Lakhs?',
        options: ['One Crore', 'Ten Crores', 'One Million', 'One Lakh'],
        correctAnswer: 0,
        explanation: 'In the Indian Place Value System, the order is Ones, Tens, Hundreds, Thousands, Ten Thousands, Lakhs, Ten Lakhs, Crores (One Crore).',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_7_science',
    title: 'Class 7 Science: Photosynthesis Assessment',
    description: 'Deeper evaluation of autotrophic nutrition and photosynthetic components.',
    category: '📚 Academic Science',
    type: 'mock',
    durationMinutes: 15,
    xpAward: 80,
    creditAward: 20,
    classLimit: 'Class 7',
    badgeText: 'PLANT LIFE',
    infoText1: '2 Questions',
    infoText2: '15 Minutes',
    buttonText: 'Start Assessment',
    questions: [
      {
        id: 'q7_s1',
        text: 'Which pigment is essential for capturing solar energy during photosynthesis in plants?',
        options: ['Carotenoids', 'Chlorophyll', 'Anthocyanin', 'Hemoglobin'],
        correctAnswer: 1,
        explanation: 'Chlorophyll is the green pigment located in chloroplasts that absorbs sunlight for photosynthesis.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_7_math',
    title: 'Class 7 Mathematics: Integers Mastery Test',
    description: 'Challenge your understanding of properties of integers and arithmetic operations.',
    category: 'Mathematics',
    type: 'practice',
    durationMinutes: 15,
    xpAward: 80,
    creditAward: 20,
    classLimit: 'Class 7',
    badgeText: 'INTEGERS PRO',
    infoText1: '2 Questions',
    infoText2: '15 Minutes',
    buttonText: 'Take Challenge',
    questions: [
      {
        id: 'q7_m1',
        text: 'Which property states that changing the order of addition does not change the sum of integers?',
        options: ['Commutative Property', 'Associative Property', 'Closure Property', 'Distributive Property'],
        correctAnswer: 0,
        explanation: 'The Commutative Property of addition states that a + b = b + a for any integers.',
        difficulty: 'Medium'
      }
    ]
  },
  {
    id: 'quiz_8_science',
    title: 'Class 8 Science: Microorganisms Evaluation',
    description: 'Detailed quiz on microbial friendship, antibiotics, and common pathogens.',
    category: '📚 Academic Science',
    type: 'mock',
    durationMinutes: 20,
    xpAward: 100,
    creditAward: 25,
    classLimit: 'Class 8',
    badgeText: 'MICRO WARRIOR',
    infoText1: '2 Questions',
    infoText2: '20 Minutes',
    buttonText: 'Begin Test',
    questions: [
      {
        id: 'q8_s1',
        text: 'Which of the following bacterium is responsible for the formation of curd from milk?',
        options: ['Rhizobium', 'Lactobacillus', 'E. Coli', 'Bacillus Anthracis'],
        correctAnswer: 1,
        explanation: 'Lactobacillus is a friendly microorganism that promotes the formation of curd by multiplying in milk.',
        difficulty: 'Easy'
      }
    ]
  },
  {
    id: 'quiz_8_math',
    title: 'Class 8 Mathematics: A Square and A Cube Challenge',
    description: 'Advance your skills on closed rational properties, reciprocal values, and zero properties.',
    category: 'Mathematics',
    type: 'practice',
    durationMinutes: 15,
    xpAward: 100,
    creditAward: 25,
    classLimit: 'Class 8',
    badgeText: 'RATIONAL PRO',
    infoText1: '2 Questions',
    infoText2: '15 Minutes',
    buttonText: 'Attempt Challenge',
    questions: [
      {
        id: 'q8_m1',
        text: 'What is the multiplicative inverse of a non-zero rational number a/b?',
        options: ['b/a', '-a/b', '1', '0'],
        correctAnswer: 0,
        explanation: 'The multiplicative inverse or reciprocal of a/b is b/a because (a/b) * (b/a) = 1.',
        difficulty: 'Medium'
      }
    ]
  }
];

export const MOCK_LEADERBOARD: LeaderboardEntry[] = [
  { uid: 'u1', displayName: 'Alex Rivera', xp: 2450, dailyStreak: 12, rank: 1, role: 'student', avatarSeed: 'alex' },
  { uid: 'u2', displayName: 'Chen Wei', xp: 2120, dailyStreak: 9, rank: 2, role: 'student', avatarSeed: 'chen' },
  { uid: 'u3', displayName: 'Priya Patel', xp: 1980, dailyStreak: 15, rank: 3, role: 'student', avatarSeed: 'priya' },
  { uid: 'u4', displayName: 'Oliver Hansen', xp: 1640, dailyStreak: 5, rank: 4, role: 'student', avatarSeed: 'oliver' },
  { uid: 'u5', displayName: 'Sophia Dubois', xp: 1550, dailyStreak: 3, rank: 5, role: 'student', avatarSeed: 'sophia' },
  { uid: 'u6', displayName: 'Marcus Aurelius', xp: 1220, dailyStreak: 8, rank: 6, role: 'student', avatarSeed: 'marcus' }
];

export const MOCK_BLOG_POSTS: BlogPost[] = [
  {
    id: 'blog_new_1',
    title: 'The Shift from Rote Learning to Deep Understanding',
    excerpt: 'Why true academic growth begins when memorization ends. Discover the 4C framework to build robust mental models instead of chasing marks.',
    content: `<section class="hero-section">
  <header>
    <h1>The Shift from Rote Learning to Deep Understanding</h1>
    <h3 class="subtitle">Why True Academic Growth Begins When Memorization Ends</h3>
    <div class="meta-info text-sm font-semibold mt-4 mb-8 text-indigo-600 dark:text-indigo-400">
      <span>Estimated reading time: 8 minutes</span> &nbsp;|&nbsp; <span>Takeaway: Shift from chasing marks to building robust mental models.</span>
    </div>
  </header>
</section>

<article>
  <section class="opening-story">
    <p>It was late Tuesday evening, just three days before the half-yearly science exam. Aryan, a bright Class 7 student, sat surrounded by textbooks, highlighters, and sticky notes. He was doing what he always did: reading the same NCERT chapter on "Nutrition in Plants" for the fifth time, trying to memorize every definition word-for-word.</p>
    <p>When asked to explain what photosynthesis meant, Aryan could recite the textbook definition flawlessly. But when asked, "What would happen to the world if plants stopped doing this today?" he froze. The connection wasn't there.</p>
    <p>Aryan wasn't failing; in fact, he regularly scored above 85%. But he was exhausted, anxious, and worst of all, he wasn't truly <em>learning</em>. He was participating in a highly stressful ritual of storing information just long enough to transfer it onto an exam paper before forgetting it completely.</p>
  </section>

  <section class="insight-card">
    <h2>The Hidden Truth About "Hard Work"</h2>
    <p>As educators, we frequently see this pattern. Many students equate "hard work" with "repeated reading." Educational psychology, however, tells a different story. Reading something repeatedly creates an <em>illusion of competence</em>. Your brain recognizes the text because it has seen it before, tricking you into believing you understand it.</p>
    <p>True learning—the kind that builds intellectual confidence and survives beyond the exam hall—requires a systematic disruption of this illusion. It requires a fundamental shift in how we approach the NCERT curriculum: moving away from surface-level retention towards building deep, interconnected mental models.</p>
  </section>

  <section class="framework-grid">
    <h2>The MCQPULSE 4C Learning Framework</h2>
    <p>To help students transition from rote memorization to authentic understanding, we have developed the <strong>4C Learning Framework</strong>. This systematic approach transforms passive reading into active mastery.</p>
    
    <div class="mt-6 space-y-6">
      <div>
        <h3 class="font-bold text-lg text-indigo-700 dark:text-indigo-400">1. Conceptualize (The "Why")</h3>
        <p><strong>Concept:</strong> Before diving into facts, understand the big picture. Why does this topic exist? What problem does it solve?</p>
        <p><strong>Example:</strong> Instead of memorizing the formula for calculating speed, understand that we need a standard way to compare how fast two different objects are moving.</p>
        <p><strong>Action Step:</strong> Before starting a new NCERT chapter, spend 5 minutes reading only the introduction and the summary.</p>
      </div>

      <div>
        <h3 class="font-bold text-lg text-indigo-700 dark:text-indigo-400">2. Connect (The "Web")</h3>
        <p><strong>Concept:</strong> New information is fragile until it is attached to existing knowledge. Learning is about building a web of interconnected ideas.</p>
        <p><strong>Example:</strong> Connecting the concept of heat transfer in physics to why you feel cold when stepping out of a warm shower.</p>
        <p><strong>Action Step:</strong> After reading a concept, write down one real-world example of it from your daily life.</p>
      </div>

      <div>
        <h3 class="font-bold text-lg text-indigo-700 dark:text-indigo-400">3. Challenge (The "Test")</h3>
        <p><strong>Concept:</strong> The brain strengthens memories not when we review them, but when we are forced to retrieve them. This is known as the testing effect.</p>
        <p><strong>Example:</strong> Closing the book and trying to explain the water cycle out loud, rather than just re-reading the diagram.</p>
        <p><strong>Action Step:</strong> Replace one hour of re-reading with 30 minutes of attempting application-based MCQs.</p>
      </div>

      <div>
        <h3 class="font-bold text-lg text-indigo-700 dark:text-indigo-400">4. Consolidate (The "Foundation")</h3>
        <p><strong>Concept:</strong> Reviewing material at strategic intervals (spaced repetition) signals to the brain that the information is important enough for long-term storage.</p>
        <p><strong>Example:</strong> Reviewing a difficult math concept 1 day, 3 days, and then 7 days after first learning it.</p>
        <p><strong>Action Step:</strong> Create brief mind maps at the end of every week summarizing what was learned.</p>
      </div>
    </div>
  </section>

  <section class="knowledge-box">
    <h3>What Successful Students Do Differently</h3>
    <table>
      <thead>
        <tr>
          <th>Average Student</th>
          <th>Consistent Learner</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Reads notes repeatedly</td>
          <td>Tests themselves frequently</td>
        </tr>
        <tr>
          <td>Focuses on what to learn</td>
          <td>Focuses on how to learn</td>
        </tr>
        <tr>
          <td>Studies for the upcoming exam</td>
          <td>Studies to build a knowledge foundation</td>
        </tr>
        <tr>
          <td>Sees mistakes as failure</td>
          <td>Uses mistakes as a diagnostic tool</td>
        </tr>
        <tr>
          <td>Asks "Will this be on the test?"</td>
          <td>Asks "How does this connect to what I already know?"</td>
        </tr>
      </tbody>
    </table>
  </section>

  <section>
    <h2>Real-Life Application</h2>
    <p>How does this look in practice for a Class 8 student studying Science?</p>
    <ul>
      <li><strong>At home:</strong> Instead of copying answers from a guide, the student reads the textbook, closes it, and tries to write the core idea in their own words.</li>
      <li><strong>In school:</strong> During lectures, rather than passively transcribing what the teacher says, they write down questions that arise from the topic.</li>
      <li><strong>During revision:</strong> They prioritize practicing mock tests and analyzing <em>why</em> an answer was wrong, rather than just tallying their final score.</li>
    </ul>
    <blockquote>"Education is not the learning of facts, but the training of the mind to think."</blockquote>
  </section>

  <section class="reflection-panel">
    <h2>Self-Reflection</h2>
    <p>Take a moment to ask yourself:</p>
    <ol class="list-decimal pl-5 space-y-2 font-medium">
      <li>Do I spend more time reading my notes, or testing my knowledge?</li>
      <li>When I get a question wrong, do I memorize the correct answer or do I try to understand the underlying concept?</li>
      <li>Can I explain the most recent topic I learned to a younger sibling in simple terms?</li>
      <li>Do I rely solely on the textbook, or do I seek out real-world examples?</li>
      <li>Am I studying to learn, or merely studying to finish?</li>
    </ol>
  </section>

  <section class="action-challenge">
    <h2>The 7-Day Active Learning Challenge</h2>
    <p>We challenge you to transform your study routine over the next week:</p>
    <ul class="space-y-2 mt-4">
      <li><strong>Days 1-2:</strong> Pick one subject. For every 30 minutes of study, spend the last 5 minutes writing a summary with the book closed.</li>
      <li><strong>Days 3-4:</strong> Before starting a new topic, take a short quiz on the previous day's material. Embrace the struggle of trying to remember.</li>
      <li><strong>Days 5-7:</strong> Take a complex concept and attempt to explain it aloud, as if you were the teacher. Identify the gaps in your own explanation.</li>
    </ul>
  </section>

  <aside class="parent-corner">
    <h2>Parent Corner: Guiding the Transition</h2>
    <p>Parents play a crucial role in shifting the focus from rote learning to deep understanding.</p>
    <ul class="space-y-3 mt-4">
      <li><strong>What to encourage:</strong> Praise effort, curiosity, and the courage to tackle difficult problems. Ask questions like, "What was the most interesting thing you learned today?" instead of "How much did you score?"</li>
      <li><strong>What to avoid:</strong> Avoid emphasizing perfect marks on practice tests. Practice is meant for making mistakes.</li>
      <li><strong>How to support:</strong> Provide a structured environment that allows for focused, distraction-free study blocks. Encourage your child to teach you a concept they learned that day.</li>
    </ul>
  </aside>

  <section class="academic-highlight mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
    <h2>MCQPULSE Insight</h2>
    <p>The journey from surface-level memorization to profound comprehension is the defining transition of middle school academics. The NCERT curriculum is beautifully designed to foster this transition, provided it is approached with the right mindset and tools.</p>
    <p>Concepts become unshakeable when reinforced through well-structured study notes, conceptual mind maps, and rigorous self-assessment. By integrating consistent MCQ practice that demands critical thinking rather than just recall, students transform information into enduring knowledge. This is the core philosophy driving every module within MCQPULSE—ensuring that every question answered is a step toward true academic mastery.</p>
  </section>
</article>
`,
    isHtml: true,
    authorName: 'MCQPulse Academics',
    authorAvatar: 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&q=80&w=150',
    coverImage: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&q=80&w=800',
    date: 'August 10, 2026',
    readTime: '8 min read',
    category: 'Learning Strategy'
  }
];

export const MOCK_FAQS: FAQItem[] = [
  {
    id: 'faq_1',
    question: 'How do Daily Streaks and Streak Freezes work?',
    answer: 'Your Daily Streak increases by 1 for every day you complete a course video, read notes, or finish at least one quiz in the Practise Hub. If you miss a day, your streak will reset to 0 unless you have a Streak Freeze purchased with credits!',
    category: 'general'
  },
  {
    id: 'faq_2',
    question: 'Are the Mock Exams designed to replicate real competitive examinations?',
    answer: 'Yes! Our mock tests are timed, graded, and formatted strictly according to the latest curricula of major international competitive assessments. They include detailed mathematical explanations for every option.',
    category: 'academic'
  },
  {
    id: 'faq_3',
    question: 'Can I access lessons offline or download PDFs?',
    answer: 'All PDF notes and cheat sheets can be fully downloaded to your device for offline reference. Video streaming, however, requires an active internet connection to securely track your XP progress.',
    category: 'technical'
  },
  {
    id: 'faq_4',
    question: 'What benefits does the MCQ Pulse™ premium membership provide?',
    answer: 'Premium subscribers unlock access to Advanced courses (e.g. Algorithms, React 19 Mastery), receive unlimited Mock Tests, earn 2x more credits on achievements, and obtain certified cryptographic certificates upon course completion.',
    category: 'billing'
  }
];

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'sub_free',
    name: 'Free Learning',
    price: '₹0',
    description: 'Perfect for every learner to get started.',
    buttonText: 'Explore Free Resources',
    features: [
      'Selected Chapter-wise MCQs',
      'Online Study Resources',
      'Daily Free Quizzes',
      'Regular Content Updates'
    ]
  },
  {
    id: 'sub_premium',
    name: 'Premium Study Packs',
    price: 'Starting from ₹149',
    description: 'Everything you need for complete exam preparation.',
    isPopular: true,
    buttonText: 'Get Premium Study Pack',
    features: [
      'Complete Chapter-wise Study Packs',
      'Online & Downloadable PDF Access',
      'Complete Exam Question Bank + Daily Quizzes',
      'Exclusive Premium Content & Regular Updates'
    ]
  }
];
